import { Star, Quote } from "lucide-react";
import { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ReviewSubmissionForm from "./ReviewSubmissionForm";
import { useSiteAssetUrl } from "@/contexts/SiteAssetsContext";

// Fallback images for existing reviews without uploaded images
import review1 from "@/assets/reviews/review-1.webp";
import review2 from "@/assets/reviews/review-2.webp";
import review3 from "@/assets/reviews/review-3.webp";
import review4 from "@/assets/reviews/review-4.webp";
import review5 from "@/assets/reviews/review-5.webp";
import review6 from "@/assets/reviews/review-6.webp";

const fallbackImages: Record<string, string> = {
  "Marcus T.": review1,
  "Destiny W.": review2,
  "Florence L.": review3,
  "Ramon S.": review4,
  "Ashley K.": review5,
  "Terrence B.": review6,
};

interface Review {
  id: string;
  name: string;
  location: string;
  rating: number;
  text: string;
  highlight: string | null;
  image_url: string | null;
  display_order: number;
}

const Reviews = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const testimonialVideoUrl = useSiteAssetUrl('testimonial_video');

  const { data: reviews = [], isLoading, isError } = useQuery({
    queryKey: ['public-reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) {
        console.error('Failed to load reviews:', error);
        throw error;
      }
      return data as Review[];
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          video.play().catch(() => {
            // Autoplay was prevented, user will need to click play
          });
        } else {
          video.pause();
        }
      });
    }, {
      threshold: 0.5
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const getReviewImage = (review: Review): string | undefined => {
    if (review.image_url) return review.image_url;
    return fallbackImages[review.name];
  };

  if (isLoading) {
    return (
      <section id="reviews" className="py-12 lg:py-16 bg-muted/30">
        <div className="container-custom">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section id="reviews" className="py-12 lg:py-16 bg-muted/30">
        <div className="container-custom">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Unable to load reviews at this time.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reviews" className="py-12 lg:py-16 bg-muted/30">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 bg-accent/20 rounded-full px-4 py-2 mb-6">
            <div className="flex -space-x-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-accent text-accent" />)}
            </div>
            <span className="text-sm font-semibold text-accent-foreground">4.9/5 Average Rating</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            What <span className="text-gradient">Real Users</span> Say
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Join thousands who've transformed their credit scores with our proven DIY system.
          </p>
          
          {/* Customer submission button */}
          <ReviewSubmissionForm />
        </div>

        {/* Featured Video Testimonial - lazy loaded */}
        <div className="mb-12">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden">
            <video 
              ref={videoRef} 
              controls 
              muted 
              playsInline 
              loop 
              autoPlay
              preload="metadata"
              className="w-full aspect-video bg-muted"
            >
              {testimonialVideoUrl && (
                <source src={testimonialVideoUrl} type="video/mp4" />
              )}
              Your browser does not support the video tag.
            </video>
            <div className="p-6 text-center">
              <div className="flex justify-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-accent text-accent" />)}
              </div>
              <p className="text-lg font-semibold text-foreground">Real Customer Success Story</p>
              <p className="text-muted-foreground">Watch how MyCreditScrub helped achieve credit goals</p>
              <p className="text-xs text-muted-foreground mt-2 italic">Individual experiences vary and are not a guarantee.</p>
            </div>
          </div>
        </div>

        {/* Reviews grid */}
        {reviews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => {
              const reviewImage = getReviewImage(review);
              return (
                <div
                  key={review.id}
                  className="group bg-card rounded-2xl overflow-hidden shadow-custom-sm hover:shadow-custom-md transition-all duration-300 hover:-translate-y-1 border border-border/50 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Customer photo - fixed aspect ratio with object-contain to prevent cropping */}
                  {reviewImage && (
                    <div className="aspect-square overflow-hidden bg-muted/50 flex items-center justify-center">
                      <img
                        src={reviewImage}
                        alt={`${review.name} - MyCreditScrub customer`}
                        width={387}
                        height={387}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}

                  <div className="p-6">
                    {/* Quote icon */}
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Quote className="w-5 h-5 text-primary" />
                    </div>

                    {/* Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                      ))}
                    </div>

                    {/* Review text */}
                    <p className="text-foreground mb-6 leading-relaxed">"{review.text}"</p>

                    {/* Highlight badge */}
                    {review.highlight && (
                      <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-3 py-1 mb-4">
                        <span className="text-sm font-bold text-primary-dark">{review.highlight}</span>
                      </div>
                    )}

                    {/* Author */}
                    <div className="pt-4 border-t border-border/50">
                      <p className="font-semibold text-foreground">{review.name}</p>
                      <p className="text-sm text-muted-foreground">{review.location}</p>
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        Individual experiences vary and are not a guarantee.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No reviews available yet.</p>
          </div>
        )}

        {/* Social proof bar */}
        <div className="mt-10 bg-card rounded-2xl p-6 shadow-custom-sm border border-border/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl lg:text-4xl font-bold text-primary-dark mb-2">10,000+</p>
              <p className="text-muted-foreground">Happy Customers</p>
            </div>
            <div>
              <p className="text-3xl lg:text-4xl font-bold text-primary-dark mb-2">4.9/5</p>
              <p className="text-muted-foreground">Average Rating</p>
            </div>
            <div>
              <p className="text-3xl lg:text-4xl font-bold text-primary-dark mb-2">Under 1 Wk </p>
              <p className="text-muted-foreground">Most users complete Round 1</p>
            </div>
            <div>
              <p className="text-3xl lg:text-4xl font-bold text-primary-dark mb-2">60 Days</p>
              <p className="text-muted-foreground">Avg. Time to Results</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
